import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock, Key, Car, Train, Phone, FileText, ParkingSquare } from 'lucide-react';
import BilingualTextarea from './BilingualTextarea';
import type { ArrivalDepartureInfo } from '../../types';

interface ArrivalDepartureEditorProps {
  value: ArrivalDepartureInfo;
  onChange: (value: ArrivalDepartureInfo) => void;
}

const ArrivalDepartureEditor: React.FC<ArrivalDepartureEditorProps> = ({ value, onChange }) => {
  const updateField = <K extends keyof ArrivalDepartureInfo>(
    field: K,
    newValue: ArrivalDepartureInfo[K]
  ) => {
    onChange({ ...value, [field]: newValue });
  };

  return (
    <div className="space-y-6">
      {/* Check-in/out Zeiten */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Check-in & Check-out Zeiten
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="checkIn">Check-in Zeit</Label>
            <Input
              id="checkIn"
              type="time"
              value={value.checkInTime}
              onChange={(e) => updateField('checkInTime', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkOut">Check-out Zeit</Label>
            <Input
              id="checkOut"
              type="time"
              value={value.checkOutTime}
              onChange={(e) => updateField('checkOutTime', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Schlüsselanweisungen */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="h-4 w-4" />
            Schlüsselanweisungen
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BilingualTextarea
            label="Schlüsselübergabe"
            value={value.keyInstructions}
            onChange={(v) => updateField('keyInstructions', v)}
            placeholder={{ de: 'Schlüsselsafe-Standort, Code-Übermittlung, etc.' }}
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Anfahrt mit Auto */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Car className="h-4 w-4" />
            Anfahrt mit dem Auto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BilingualTextarea
            label="Fahrtbeschreibung"
            value={value.directionsByCar}
            onChange={(v) => updateField('directionsByCar', v)}
            placeholder={{ de: 'Autobahnausfahrten, Abbiegungen, Parkhinweise...' }}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Anfahrt mit Zug */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Train className="h-4 w-4" />
            Anfahrt mit dem Zug
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BilingualTextarea
            label="Zugverbindung"
            value={value.directionsByTrain}
            onChange={(v) => updateField('directionsByTrain', v)}
            placeholder={{ de: 'Bahnhofsname, Verbindungen, Fussweg...' }}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Parkplatz */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <ParkingSquare className="h-4 w-4" />
            Parkplatz
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BilingualTextarea
            label="Parkplatzinformationen"
            value={value.parkingInfo}
            onChange={(v) => updateField('parkingInfo', v)}
            placeholder={{ de: 'Anzahl Plätze, Standort, eventuelle Einschränkungen...' }}
            rows={2}
          />
        </CardContent>
      </Card>

      {/* Notfallkontakt */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="h-4 w-4" />
            Notfallkontakt
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="contact">Telefonnummer</Label>
            <Input
              id="contact"
              type="tel"
              value={value.contactNumber}
              onChange={(e) => updateField('contactNumber', e.target.value)}
              placeholder="+41 79 123 45 67"
            />
          </div>
        </CardContent>
      </Card>

      {/* Zusätzliche Hinweise */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Zusätzliche Hinweise
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BilingualTextarea
            label="Weitere Informationen"
            value={value.additionalNotes || { en: '', de: '' }}
            onChange={(v) => updateField('additionalNotes', v)}
            placeholder={{ de: 'WLAN-Passwort, Alarmcodes, spezielle Anweisungen...' }}
            rows={3}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default ArrivalDepartureEditor;
